import Vue from 'vue'
import Router from 'vue-router'
import store from './store';

Vue.use(Router)

export default new Router({
    mode: 'history',
    base: process.env.BASE_URL,

	/*
	 * | ------------------------
	 * | Simulate native-like scroll behavior when navigating to a new
	 * | route and using back/forward buttons.
	 * | ------------------------
	 * |
	 */
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        } else {
            return { x: 0, y: 0 }
        }
    },

	/*
	 * | ------------------------
	 * | All router page routes
	 * | Contains all routes and their matching components
	 * | ------------------------
	 * |
	 */
    routes: [
        {
            path: '/',
            component: () => import(`@/views/_index.vue`),
            children: [
                {
                    path: '/',
                    beforeEnter: guardGuest,
                    component: () => lazyLoadView(import(`@/views/guest/_index.vue`)),
                    children: [
                        {
                            path: '/',
                            name: 'select',
                            component: () => lazyLoadView(import(`@/views/guest/login`)),
                        },
                        {
                            path: '/login',
                            name: 'login',
                            component: () => lazyLoadView(import(`@/views/guest/login`)),
                        },
                        {
                            path: '/password/forgot',
                            name: 'password-forgot',
                            component: () => lazyLoadView(import(`@/views/guest/password-forgot`)),
                        },
                        {
                            path: '/password/reset',
                            name: 'password-reset',
                            component: () => lazyLoadView(import(`@/views/guest/password-reset`)),
                        },
                        {
                            path: '/register',
                            name: 'register',
                            component: () => lazyLoadView(import(`@/views/guest/register`)),
                        },
                        {
                            path: '/psychologists/register',
                            name: 'psychologists-register',
                            component: () => lazyLoadView(import(`@/views/guest/psychologist-register`)),
                        }
                    ]
                },
                {
                    path: '/',
                    name: 'base',
                    beforeEnter: guardAuth,
                    component: () => import(`@/views/auth/_index`),
                    children: [
                        {
                            path: '/onboarding/',
                            component: () => lazyLoadView(import(`@/views/auth/onboarding/_index`)),
                            children: [
                                {
                                    path: 'profession',
                                    name: 'onboarding-profession',
                                    component: () => lazyLoadView(import(`@/views/auth/onboarding/profession`)),
                                },
                                {
                                    path: 'sleepprofile',
                                    name: 'onboarding-sleepprofile',
                                    component: () => lazyLoadView(import(`@/views/auth/onboarding/sleepprofile`)),
                                },
                                {
                                    path: 'personal',
                                    name: 'onboarding-personal',
                                    component: () => lazyLoadView(import(`@/views/auth/onboarding/personal`)),
                                },
                                {
                                    path: 'medical',
                                    name: 'onboarding-medical',
                                    component: () => lazyLoadView(import(`@/views/auth/onboarding/medical`)),
                                },
                            ],
                        },
                        {
                            path: '/p/',
                            component: () => lazyLoadView(import(`@/views/auth/platform/_index`)),
                            props: route => ({ showPayment: ('payment' in route.query) }),
                            children: [
                                {
                                    path: 'notifications',
                                    name: 'notifications',
                                    component: () => lazyLoadView(import(`@/views/auth/platform/notifications`)),
                                },
                                {
                                    path: 'schedule',
                                    name: 'sleep-schedule',
                                    component: () => lazyLoadView(import(`@/views/auth/platform/schedule`)),
                                },
                                {
                                    path: 'schedule/user',
                                    name: 'user-schedule',
                                    component: () => lazyLoadView(import(`@/views/auth/platform/schedule-user`)),
                                },
                                {
                                    path: 'schedule/create',
                                    name: 'schedule-details',
                                    component: () => lazyLoadView(import(`@/views/auth/platform/schedule-details`)),
                                },
                                {
                                    path: 'schedule/:id',
                                    name: 'schedule-edit',
                                    component: () => lazyLoadView(import(`@/views/auth/platform/schedule-details`)),
                                },
                                {
                                    path: 'tips',
                                    name: 'tips',
                                    component: () => lazyLoadView(import(`@/views/auth/platform/tips`)),
                                },
                                {
                                    path: 'tips/:id',
                                    name: 'tip-detail',
                                    component: () => lazyLoadView(import(`@/views/auth/platform/tip-details`)),
                                },
                                {
                                    path: '/user/',
                                    component: () => lazyLoadView(import(`@/views/auth/platform/user/_index`)),
                                    children: [
                                        {
                                            path: 'settings',
                                            name: 'user-settings',
                                            component: () => lazyLoadView(import(`@/views/auth/platform/user/settings`)),
                                            meta: {
                                                profile_side: true
                                            },
                                        },
                                        {
                                            path: 'habits',
                                            name: 'user-habits',
                                            component: () => lazyLoadView(import(`@/views/auth/platform/user/habits`)),
                                            meta: {
                                                profile_side: true
                                            },
                                        },
                                        {
                                            path: 'medical',
                                            name: 'user-medical',
                                            component: () => lazyLoadView(import(`@/views/auth/platform/user/medical`)),
                                            meta: {
                                                profile_side: true
                                            },
                                        },
                                        {
                                            path: 'license',
                                            name: 'user-license',
                                            component: () => lazyLoadView(import(`@/views/auth/platform/user/license`)),
                                            meta: {
                                                profile_side: true
                                            },
                                        },
                                        {
                                            path: 'password',
                                            name: 'user-password',
                                            component: () => lazyLoadView(import(`@/views/auth/platform/user/password`)),
                                            meta: {
                                                profile_side: true
                                            },
                                        },
                                    ]
                                }
                            ]
                        },
                        {
                            path: '/a/',
                            component: () => lazyLoadView(import(`@/views/auth/admin/_index`)),
                            children: [
                                {
                                    path: 'dashboard',
                                    name: 'admin-dashboard',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/dashboard`)),
                                    meta: {
                                        admin: true
                                    },
                                },

                                /*
                                * Patients
                                *
                                */
                                {
                                    path: 'patients',
                                    name: 'admin-patients',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/patients/index`)),
                                    meta: {
                                        admin: true
                                    },
                                },
                                {
                                    path: 'patients/add',
                                    name: 'admin-add-patient',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/patients/add`)),
                                    meta: {
                                        admin: true
                                    },
                                },
                                {
                                    path: 'patients/:id',
                                    name: 'admin-show-patient',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/patients/show`)),
                                    meta: {
                                        admin: true
                                    },
                                },

                                /*
                                * Advice
                                *
                                */
                                {
                                    path: 'tips',
                                    name: 'admin-tips',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/tips/index`)),
                                    meta: {
                                        admin: true
                                    },
                                },
                                {
                                    path: 'tips/:id',
                                    name: 'admin-tips-show',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/tips/show`)),
                                    meta: {
                                        admin: true
                                    },
                                },

                                /*
                                * Codes
                                *
                                */
                                {
                                    path: 'codes',
                                    name: 'admin-codes',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/codes/index`)),
                                    meta: {
                                        admin: true
                                    },
                                },
                                {
                                    path: 'codes/add',
                                    name: 'admin-codes-add',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/codes/show`)),
                                    meta: {
                                        admin: true
                                    },
                                },
                                {
                                    path: 'codes/:id',
                                    name: 'admin-codes-show',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/codes/show`)),
                                    meta: {
                                        admin: true
                                    },
                                },

                                /*
                                * Psychologists
                                *
                                */
                                {
                                    path: 'psychologists',
                                    name: 'admin-psychologists',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/psychologists/index`)),
                                    meta: {
                                        admin: true
                                    },
                                },
                                {
                                    path: 'psychologists/add',
                                    name: 'admin-psychologists-add',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/psychologists/show`)),
                                    meta: {
                                        admin: true
                                    },
                                },
                                {
                                    path: 'psychologists/:id',
                                    name: 'admin-psychologists-show',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/psychologists/show`)),
                                    meta: {
                                        admin: true
                                    },
                                },

                                /*
                                * Admins
                                *
                                */
                                {
                                    path: 'admins',
                                    name: 'admin-admins',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/admins/index`)),
                                    meta: {
                                        admin: true
                                    },
                                },
                                {
                                    path: 'admins/:id',
                                    name: 'admin-show-admins',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/admins/show`)),
                                    meta: {
                                        admin: true
                                    },
                                },
                                {
                                    path: 'admins/add',
                                    name: 'admin-create-admins',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/admins/show`)),
                                    meta: {
                                        admin: true
                                    },
                                },
                                {
                                    path: 'profile',
                                    name: 'admin-profile',
                                    component: () => lazyLoadView(import(`@/views/auth/admin/admins/profile`)),
                                    meta: {
                                        admin: true
                                    },
                                },
                            ]
                        },
                        {
                            path: 'p/transactions/:id',
                            name: 'transactions-show',
                            beforeEnter: guardAuth,
                            component: () => import(`@/views/auth/transactions/transaction-detail`),
                        }
                    ]
                },
                {
                    path: '/logout',
                    name: 'logout',
                    beforeEnter: (to, from, next) => {
                        store.dispatch('user/logout').then(() => {
                            next({ name: 'login' })
                        })
                    }
                }
            ],
        },
    ],
})

/**
 * Guard guest middleware
 * Redirect to the login page when the user is not logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
function guardGuest(to, from, next) {

    if (store.getters['user/isLoggedIn'])
        next({ name: 'sleep-schedule' })

    next()
}

/**
 * Guard auth middleware
 * Redirect to the login page when the user is not logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
function guardAuth(to, from, next) {
    store.dispatch('user/me').then(
        () => {
            if (!store.getters['user/isLoggedIn'])
                next({ name: 'login' })

            let currentUser = store.getters['user/currentUser']

            if (!currentUser.email) next({ name: 'logout' })

            if (!currentUser.user && currentUser.psychologist && !to.meta.admin)
                next({ name: 'admin-dashboard' })

            if (!currentUser.user && currentUser.permissions.length > 0 && !to.meta.admin)
                next({ name: 'admin-dashboard' })
            else {
                if (currentUser.user && !currentUser.user.onboarding_complete) {
                    if (to.name !== 'onboarding-profession' && to.name !== 'onboarding-sleepprofile' && to.name !== 'onboarding-personal' && to.name !== 'onboarding-medical')
                        next({ name: 'onboarding-profession' })
                    else
                        next()
                }
                else
                    next()
            }
        },
        (e) => {
            if (e === 'auth.unauthorized') {
                store.dispatch('user/logout')
                next({
                    name: 'login',
                    query: {
                        redirect: to.fullPath,
                    },
                })
            }
        }
    )

}

/**
 * Lazy-loads view components, but with better UX. A loading view
 * will be used if the component takes a while to load, falling
 * back to a timeout view in case the page fails to load. You can
 * use this component to lazy-load a route with:
 *
 * component: () => lazyLoadView(import('@views/my-view'))
 *
 * @param {AsyncView} The view component
 */
function lazyLoadView(AsyncView) {
    const AsyncHandler = () => ({
        component: AsyncView,
        delay: 400,
        timeout: 10000,
    })

    return Promise.resolve({
        functional: true,
        render(h, { data, children }) {
            return h(AsyncHandler, data, children)
        },
    })
}


